// Form value yardımcıları — M4 polish + M15 D1 formatter konsolidasyonu.
// FE (selectSectionProgress, PriorYearHint) + BE (getMissingFields, prompt-assembler, docx)
// TEK kaynak kullansın. Eskiden dört ayrı kopya vardı (prompt/docx/hint/renderer) ve
// dördü farklı davranıyordu — tekli seçim AI'ya "Seçim: opt1" gidiyordu (§1.1 defect).
//
// Bu paket yalnızca zod'a bağlı ve tarayıcıda da çalışır — logger import EDİLMEZ;
// gözlemlenebilirlik `opts.onFallback` callback'iyle dışarı verilir (D7).

import { z } from "zod";
import { CompositePartSchema, type CompositePart } from "../schemas/questions.js";
import type { QuestionType } from "../schemas/questions.js";

// ---------------------------------------------------------------------------
// Doluluk kontrolleri
// ---------------------------------------------------------------------------

// Kritik: repeater/table için shallow `Array.isArray && length > 0` yetmez —
// kullanıcı blok/satır ekleyip içini boş bırakırsa "dolu" sayılıyordu.
// Deep check: en az bir blok'ta en az bir alt alan gerçekten dolu olmalı.
// K3 (Finar UX P2): yalnız boşluktan oluşan metin BOŞ sayılır — eksik/ilerleme/üretim
// kapısı BE+FE bu tek yardımcıdan geçer. Asgari uzunluk kuralı YOK ("BİM", "Evet" geçerli).
export function isFormValueFilled(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v === "boolean") return true;
  if (Array.isArray(v)) {
    if (v.length === 0) return false;
    // Repeater/Table (object array) + multi/file (primitive array):
    // en az bir element dolu olmalı.
    return v.some((item) => {
      if (item !== null && typeof item === "object" && !Array.isArray(item)) {
        return Object.values(item as Record<string, unknown>).some(isFormValueFilled);
      }
      return isFormValueFilled(item);
    });
  }
  if (typeof v === "object") {
    // Percent ({ catId: number }) + composite ({ partKey: değer }) —
    // en az bir alanda değer girilmiş olmalı.
    return Object.values(v as Record<string, unknown>).some(isFormValueFilled);
  }
  return false;
}

/**
 * Composite parçalarına tek tipli okuyucu (D8). `QuestionSnapshotSchema.options`
 * bilerek `z.unknown()` — donmuş snapshot'lar Zod'dan geçirilmez; composite erişimi
 * her yerde bu fonksiyondan yapılır: cast tek yerde, testli, bozuk şekilde null.
 */
const CompositePartsArraySchema = z.array(CompositePartSchema).min(1);

export function readCompositeParts(options: unknown): CompositePart[] | null {
  if (options === null || typeof options !== "object" || Array.isArray(options)) return null;
  const o = options as { type?: unknown; parts?: unknown };
  if (o.type !== "composite") return null;
  const parsed = CompositePartsArraySchema.safeParse(o.parts);
  return parsed.success ? parsed.data : null;
}

/**
 * Snapshot-aware "cevap tam mı?" check. M5.5 custom input kuralı:
 * single/multi'de allowCustomInput=true olan opsiyon seçildiyse customInput de dolu
 * olmalı, yoksa missing sayılır.
 * M15 composite kuralı: required parçaların hepsi (kendi tipinin kuralıyla) dolu;
 * hiç required parça yoksa en az bir parça dolu. Custom-input kuralı parçaya iner.
 * Diğer tipler için `isFormValueFilled` fallback'i.
 */
export function isAnswerComplete(
  value: unknown,
  question: {
    type: string;
    options?: unknown;
  },
): boolean {
  if (!isFormValueFilled(value)) return false;

  if (question.type === "composite") {
    const parts = readCompositeParts(question.options);
    if (!parts) return false; // tanım bozuk — tamamlanmış sayma
    // Tip değişimi sonrası eski (obje olmayan) değer → eksik, çökme yok (§7.2)
    if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
    const v = value as Record<string, unknown>;
    const partComplete = (p: CompositePart): boolean =>
      isAnswerComplete(v[p.key], { type: p.options.type, options: p.options });
    const requiredParts = parts.filter((p) => p.required);
    if (requiredParts.length > 0) return requiredParts.every(partComplete);
    return parts.some(partComplete);
  }

  const choices =
    (question.options as { choices?: { id: string; allowCustomInput?: boolean }[] } | null)
      ?.choices ?? [];
  const allowMap = new Map(choices.map((c) => [c.id, c.allowCustomInput === true]));

  if (question.type === "single" && value && typeof value === "object") {
    const v = value as { optionId: string; customInput?: string };
    if (allowMap.get(v.optionId) && !(v.customInput && v.customInput.trim().length > 0)) {
      return false; // "Diğer" seçildi ama metin yok
    }
  }
  if (question.type === "multi" && value && typeof value === "object") {
    const v = value as { optionIds: string[]; customInputs?: Record<string, string> };
    for (const optId of v.optionIds) {
      if (allowMap.get(optId)) {
        const text = v.customInputs?.[optId];
        if (!text || text.trim().length === 0) return false;
      }
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Coverage scaffolding sözleşmesi (D3)
// ---------------------------------------------------------------------------

/**
 * Prompt bloğundaki YAPISAL satırlar: "=== ... ===" blok başlıkları, "[q.code] label"
 * soru başlıkları ve "[part:key] label" parça başlıkları. Bunlar anchor kaynağı DEĞİL.
 * Hem `formatFormValue` (satır üretici) hem backend `stripBlockScaffolding` (satır
 * ayıklayıcı) BU fonksiyonu kullanır — kural tek yerde, drift derleme dışı ama testli
 * (form-value.test.ts sözleşme testi + coverage.test.ts).
 */
export function isScaffoldingLine(line: string): boolean {
  const t = line.trim();
  if (t.startsWith("===")) return true;
  if (/^\[[^\]]+\]/.test(t)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// formatFormValue — tek değer formatlayıcı (D1)
// ---------------------------------------------------------------------------

export interface FormatQuestionRef {
  /** QuestionType beklenir ama snapshot'tan gelen bilinmeyen tip de fırlatmaz. */
  type: string;
  options?: unknown;
  code?: string;
}

export interface FormatFallbackInfo {
  questionCode?: string | undefined;
  expectedType: string;
  actualShape: string;
}

export interface FormatValueOptions {
  /** Boş değer metni. Prompt "(boş)", DOCX "[BİLGİ GİRİLMEDİ]", PriorYearHint "—". */
  emptyText?: string;
  /**
   * Bozuk/tanınmayan şekilde çağrılır (değer içeriği ASLA geçmez — sadece şekil bilgisi).
   * Backend pino'ya (`form_value.format_fallback`), FE console.warn'a bağlayabilir.
   */
  onFallback?: (info: FormatFallbackInfo) => void;
}

interface FormatCtx {
  emptyText: string;
  onFallback?: ((info: FormatFallbackInfo) => void) | undefined;
  questionCode?: string | undefined;
}

/** Şekil eşleşmezse null döner → çağıran fallback'e düşer. */
type TypeFormatter = (value: unknown, options: unknown, ctx: FormatCtx) => string | null;

function shapeOf(v: unknown): string {
  if (v === null) return "null";
  if (Array.isArray(v)) return "array";
  return typeof v;
}

function safeJson(v: unknown): string {
  try {
    const s = JSON.stringify(v, null, 2);
    if (typeof s === "string") return s;
  } catch {
    // BigInt vb. — String()'e düş
  }
  try {
    return String(v);
  } catch {
    return "(gösterilemiyor)";
  }
}

function escapeCell(s: string): string {
  return s.replace(/\|/g, "\\|");
}

function resolveChoiceLabel(list: unknown, id: string): string {
  if (Array.isArray(list)) {
    for (const item of list) {
      if (
        item !== null &&
        typeof item === "object" &&
        (item as { id?: unknown }).id === id &&
        typeof (item as { label?: unknown }).label === "string"
      ) {
        return (item as { label: string }).label;
      }
    }
  }
  return id; // D11 — etiket çözülemezse çıplak kimlik, uydurma yok
}

/** Soru bağlamı yokken/şekil tanınmazken ham davranış (eski prompt formatValue mirası). */
function formatGeneric(v: unknown): string {
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "boolean") return v ? "Evet" : "Hayır";
  if (typeof v === "object" && v !== null && !Array.isArray(v)) {
    const obj = v as Record<string, unknown>;
    if (typeof obj.optionId === "string") {
      const parts = [`Seçim: ${obj.optionId}`];
      if (typeof obj.customInput === "string" && obj.customInput.length > 0) {
        parts.push(`Açıklama: ${obj.customInput}`);
      }
      return parts.join(" · ");
    }
    if (Array.isArray(obj.optionIds)) {
      const parts = [`Seçimler: ${(obj.optionIds as unknown[]).map(String).join(", ")}`];
      const customs = obj.customInputs;
      if (customs !== null && typeof customs === "object" && !Array.isArray(customs)) {
        for (const [id, text] of Object.entries(customs as Record<string, unknown>)) {
          if (typeof text === "string" && text.length > 0) parts.push(`  ${id}: ${text}`);
        }
      }
      return parts.join("\n");
    }
  }
  return safeJson(v);
}

/**
 * Tablo/repeater HÜCRE formatlayıcısı (D14). Hücre tipi QuestionType değil —
 * sütun tipi (text/numeric/date) + opsiyonel `unit`. "1250" değil "1250 ton CO2e".
 * Boş hücre "" döner — satır kurucusu atlar.
 */
export function formatCell(
  value: unknown,
  column: {
    id?: string | undefined;
    label?: string | undefined;
    type?: string | undefined;
    unit?: string | undefined;
  },
): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "";
    return column.unit ? `${String(value)} ${column.unit}` : String(value);
  }
  if (typeof value === "boolean") return value ? "Evet" : "Hayır";
  return safeJson(value);
}

function formatObjectRows(
  rows: unknown[],
  fields: { id: string; label: string; type?: string | undefined; unit?: string | undefined }[],
  ctx: FormatCtx,
): string {
  const lines: string[] = [];
  for (const row of rows) {
    if (row === null || typeof row !== "object" || Array.isArray(row)) continue;
    const r = row as Record<string, unknown>;
    const cells: string[] = [];
    for (const f of fields) {
      const text = formatCell(r[f.id], f);
      if (text === "") continue; // boş hücre atlanır — "undefined" yazılmaz
      cells.push(`${f.label}: ${escapeCell(text)}`);
    }
    if (cells.length > 0) lines.push(`- ${cells.join(" | ")}`);
  }
  return lines.length > 0 ? lines.join("\n") : ctx.emptyText;
}

function readFieldDefs(
  options: unknown,
  key: "columns" | "subFields",
): { id: string; label: string; type?: string | undefined; unit?: string | undefined }[] | null {
  const list = (options as Record<string, unknown> | null | undefined)?.[key];
  if (!Array.isArray(list) || list.length === 0) return null;
  const out: {
    id: string;
    label: string;
    type?: string | undefined;
    unit?: string | undefined;
  }[] = [];
  for (const item of list) {
    if (item === null || typeof item !== "object") return null;
    const f = item as { id?: unknown; label?: unknown; type?: unknown; unit?: unknown };
    if (typeof f.id !== "string" || typeof f.label !== "string") return null;
    out.push({
      id: f.id,
      label: f.label,
      type: typeof f.type === "string" ? f.type : undefined,
      unit: typeof f.unit === "string" ? f.unit : undefined,
    });
  }
  return out;
}

function formatPartValue(partValue: unknown, part: CompositePart, ctx: FormatCtx): string {
  if (!isFormValueFilled(partValue)) return ctx.emptyText;
  const formatter = FORMATTERS[part.options.type];
  const out = formatter(partValue, part.options, ctx);
  if (out === null) {
    ctx.onFallback?.({
      questionCode: ctx.questionCode,
      expectedType: `composite:${part.key}:${part.options.type}`,
      actualShape: shapeOf(partValue),
    });
    return formatGeneric(partValue);
  }
  return out;
}

/**
 * Tip başına formatlayıcı haritası (D7 — switch değil): yeni QuestionType eklenince
 * eksik anahtar DERLEME hatası verir, sessiz düşüş imkansız.
 */
const FORMATTERS: Record<QuestionType, TypeFormatter> = {
  text: (v) => (typeof v === "string" ? v : null),
  numeric: (v, options) => {
    if (typeof v !== "number" || !Number.isFinite(v)) return null;
    const unit = (options as { unit?: unknown } | null | undefined)?.unit;
    return typeof unit === "string" && unit.length > 0 ? `${String(v)} ${unit}` : String(v);
  },
  single: (v, options) => {
    if (v === null || typeof v !== "object" || Array.isArray(v)) return null;
    const obj = v as { optionId?: unknown; customInput?: unknown };
    if (typeof obj.optionId !== "string") return null;
    const choices = (options as { choices?: unknown } | null | undefined)?.choices;
    const parts = [`Seçim: ${resolveChoiceLabel(choices, obj.optionId)}`];
    if (typeof obj.customInput === "string" && obj.customInput.length > 0) {
      parts.push(`Açıklama: ${obj.customInput}`);
    }
    return parts.join(" · ");
  },
  multi: (v, options) => {
    if (v === null || typeof v !== "object" || Array.isArray(v)) return null;
    const obj = v as { optionIds?: unknown; customInputs?: unknown };
    if (!Array.isArray(obj.optionIds)) return null;
    const choices = (options as { choices?: unknown } | null | undefined)?.choices;
    const ids = obj.optionIds.filter((x): x is string => typeof x === "string");
    const parts = [`Seçimler: ${ids.map((id) => resolveChoiceLabel(choices, id)).join(", ")}`];
    const customs = obj.customInputs;
    if (customs !== null && typeof customs === "object" && !Array.isArray(customs)) {
      for (const [id, text] of Object.entries(customs as Record<string, unknown>)) {
        if (typeof text === "string" && text.length > 0) {
          parts.push(`  ${resolveChoiceLabel(choices, id)}: ${text}`);
        }
      }
    }
    return parts.join("\n");
  },
  date: (v) => (typeof v === "string" ? v : null),
  file: (v) => {
    if (!Array.isArray(v)) return null;
    return `${String(v.length)} dosya eklendi`;
  },
  table: (v, options, ctx) => {
    if (!Array.isArray(v)) return null;
    const cols = readFieldDefs(options, "columns");
    if (!cols) return null;
    return formatObjectRows(v, cols, ctx);
  },
  percent: (v, options) => {
    if (v === null || typeof v !== "object" || Array.isArray(v)) return null;
    const categories = (options as { categories?: unknown } | null | undefined)?.categories;
    const entries = Object.entries(v as Record<string, unknown>).filter(
      ([, val]) => typeof val === "number" && Number.isFinite(val),
    );
    if (entries.length === 0) return null;
    return entries
      .map(([id, val]) => `${resolveChoiceLabel(categories, id)}: ${String(val)}%`)
      .join(", ");
  },
  repeater: (v, options, ctx) => {
    if (!Array.isArray(v)) return null;
    const fields = readFieldDefs(options, "subFields");
    if (!fields) return null;
    return formatObjectRows(v, fields, ctx);
  },
  composite: (v, options, ctx) => {
    if (v === null || typeof v !== "object" || Array.isArray(v)) return null;
    const parts = readCompositeParts(options);
    if (!parts) return null;
    const record = v as Record<string, unknown>;
    const lines: string[] = [];
    for (const part of parts) {
      // Parça başlığı yapısal satır — isScaffoldingLine ile atılır, anchor üretmez (D3).
      lines.push(`[part:${part.key}] ${part.label}`);
      lines.push(formatPartValue(record[part.key], part, ctx));
    }
    return lines.join("\n");
  },
};

/**
 * Tek değer formatlayıcı (D1). TOTAL'dir — hiçbir girdide fırlatmaz (prompt kurulumunda
 * bir exception bölüm üretimini komple öldürür). `question` verilmezse ham davranış;
 * verilir + şekil eşleşirse tip-farkında okunur çıktı (seçenek ETİKETLERİ, birimler,
 * satır-bazlı tablolar, [part:key] parça başlıkları).
 */
export function formatFormValue(
  value: unknown,
  question?: FormatQuestionRef,
  opts?: FormatValueOptions,
): string {
  const emptyText = opts?.emptyText ?? "(boş)";
  if (!isFormValueFilled(value)) return emptyText;

  if (!question) return formatGeneric(value);

  const ctx: FormatCtx = {
    emptyText,
    onFallback: opts?.onFallback,
    questionCode: question.code,
  };

  // Ş3 — own-property + fonksiyon guard'ı: prototip zinciri anahtarları ("constructor"
  // vb.) fonksiyon-olmayan değer döndürüp totality sözleşmesini kırabilirdi.
  const formatter = Object.prototype.hasOwnProperty.call(FORMATTERS, question.type)
    ? (FORMATTERS as Record<string, TypeFormatter | undefined>)[question.type]
    : undefined;
  if (typeof formatter !== "function") {
    opts?.onFallback?.({
      questionCode: question.code,
      expectedType: question.type,
      actualShape: shapeOf(value),
    });
    return formatGeneric(value);
  }

  const out = formatter(value, question.options, ctx);
  if (out === null) {
    opts?.onFallback?.({
      questionCode: question.code,
      expectedType: question.type,
      actualShape: shapeOf(value),
    });
    return formatGeneric(value);
  }
  return out;
}
